import type { TasksKanbanTask } from '@entities/task'
import type { KanbanTaskCreateSubmitPayload } from '../../api/kanban-task-create-submit.hook'

import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'
import { useToast } from '@shared/ui/toast'

import { useKanbanTaskCreateSubmitApi } from '../../api/kanban-task-create-submit.hook'
import { useTasksKanbanStore } from '../store'

interface ButtonTaskCreateReturn {
  setSubmitCreate: () => Promise<void>
}

const isFormValid = (payload: KanbanTaskCreateSubmitPayload): boolean => {
  return !!payload.task_type
    && !!payload.status
    && !!payload.deadline
}

export const useButtonTaskCreate = (): ButtonTaskCreateReturn => {
  const tasksKanbanStore = useTasksKanbanStore()
  const toast = useToast()
  const route = useRoute()

  const getSourceTaskId = (): number | null => {
    const value = route.query.source_task_id
    if (typeof value !== 'string') return null

    const id = Number(value)
    return Number.isSafeInteger(id) && id > 0 ? id : null
  }

  const buildPayload = (): KanbanTaskCreateSubmitPayload => ({
    task_type: tasksKanbanStore.getTaskType ?? '',
    status: tasksKanbanStore.getStage!,
    priority: tasksKanbanStore.getPriority,
    deadline: tasksKanbanStore.getDeadline,
    assignee_id: tasksKanbanStore.getAssignee?.id ?? null,
    customer_id: tasksKanbanStore.getCustomer?.id ?? null,
    related_to_id: tasksKanbanStore.getRelatedTo?.id ?? null,
    source_task_id: getSourceTaskId(),
    extra_fields: tasksKanbanStore.getExtraFieldsPayload,
  })

  const setSubmitCreate = async (): Promise<void> => {
    tasksKanbanStore.setSubmitted(true)

    const payload = buildPayload()

    if (!isFormValid(payload)) return

    if (!tasksKanbanStore.isExtraFieldsValid) {
      toast.warning({
        title: 'Attention!',
        description: 'Please fill in all Additional details fields.',
      })
      return
    }

    await useKanbanTaskCreateSubmitApi({
      task: payload,
      comment: tasksKanbanStore.getComment,
      onSuccess: (task: TasksKanbanTask) => {
        tasksKanbanStore.clearCreationFields()
        const url = resolveUrl(AppRoute.TasksKanbanInfo, { id: task.id })
        void navigateTo(url)
      },
    })
  }

  return {
    setSubmitCreate,
  }
}
